import { evaluateFormula } from "@yjsgames/formula-evaluator";
import { RARITY_ORDER, type ModuleRarity } from "../modules/ProjectileModule.js";
import type { ModuleEffectData } from "../modules/ModuleEffect.js";

/**
 * 파이프라인 강화(downstream buff) 순수 로직 — Phaser 비의존(단위 테스트 대상).
 *
 * 일부 모듈(촉매/정밀)은 자기 spec을 변형하는 대신 경로상 **"바로 다음 1개"** 모듈을 강화한다:
 *  - enhanceNextRarity: 다음 모듈의 유효 등급을 +N단계(legendary는 더 못 올려 clamp) → 더 강한 effect 적용.
 *  - enhanceNextChance: 다음 모듈의 발동확률(chance)을 +X 가산.
 *
 * 이는 "다른 모듈에 영향"이라 ProjectileModule.apply(자기 spec 변형) 모델 밖이다. CombatSystem.buildSpec이
 * 경로순 모듈을 만들 때 이 함수로 (적용등급, chanceBonus)를 산출 → 등급은 registry.create로 재생성,
 * chanceBonus는 DefinitionModule.apply에 주입한다. 배치 순서가 전략이 되는 메커니즘(다음에 강한 모듈을 둘수록 이득).
 */

/** 등급을 steps단계 상승시킨다 (legendary 초과는 clamp, 음수/0은 변화 없음). RARITY_ORDER가 SSoT. */
export function raiseRarity(rarity: ModuleRarity, steps: number): ModuleRarity {
  if (steps <= 0) return rarity;
  const i = RARITY_ORDER.indexOf(rarity);
  if (i < 0) return rarity;
  return RARITY_ORDER[Math.min(i + steps, RARITY_ORDER.length - 1)]!;
}

/** effect의 enhanceNext 메타를 현재 레벨로 평가한 결과 (둘 다 미기재면 0 = buff 없음). */
export interface EnhanceMeta {
  /** 다음 모듈 등급 상승 단계 (>=0, 정수). */
  readonly rarityDelta: number;
  /** 다음 모듈 발동확률 가산 (>=0). */
  readonly chanceBonus: number;
}

/** effect의 enhanceNextRarity/enhanceNextChance를 평가 (없으면 0). 음수는 0으로 정규화. */
export function readEnhanceMeta(
  effect: ModuleEffectData | undefined,
  level: number,
): EnhanceMeta {
  if (!effect) return { rarityDelta: 0, chanceBonus: 0 };
  const ev = (e: string | undefined): number => (e ? evaluateFormula(e, { level }) : 0);
  return {
    rarityDelta: Math.max(0, Math.floor(ev(effect.enhanceNextRarity))),
    chanceBonus: Math.max(0, ev(effect.enhanceNextChance)),
  };
}

/** 여러 effect의 enhance 메타를 합산한다. 숙련도 effect가 다음 모듈 강화량을 보탤 때 사용한다. */
export function combineEnhanceMeta(
  effects: readonly (ModuleEffectData | undefined)[],
  level: number,
): EnhanceMeta {
  return effects.reduce<EnhanceMeta>(
    (acc, effect) => {
      const meta = readEnhanceMeta(effect, level);
      return {
        rarityDelta: acc.rarityDelta + meta.rarityDelta,
        chanceBonus: acc.chanceBonus + meta.chanceBonus,
      };
    },
    { rarityDelta: 0, chanceBonus: 0 },
  );
}

/**
 * 이웃 효과 재발동(echo) 메타 — 경로상 이전/다음 모듈 효과를 한 번 더 발동시킬 확률(0~1).
 *
 * **enhance와의 차이 — resolve 불필요:**
 * enhance는 "자기 다음 모듈에게 buff를 전달"하는 전파 구조이므로
 * `resolvePipelineEnhancements`가 경로 전체를 순회하며 (이전→현재) 방향으로 buff를 적용해야 한다.
 * echo는 "자기 위치에서 이전/다음 인덱스를 직접 가리켜 재발동"하는 구조이므로
 * 경로 전체 사전 처리 없이 build 루프가 i−1·i+1 인덱스에서 즉석으로 소비한다.
 *
 * **소비자:** `ProjectileSpecBuilder`(또는 `CombatSystem.buildSpec`) — 경로순 모듈을 순회하며
 * `readEchoMeta`를 호출하고 `prevChance`/`nextChance`로 이웃 모듈의 `ModuleEffect.apply`를
 * 추가 실행한다.
 */
export interface EchoMeta {
  /** 이전(i-1) 모듈 효과 재발동 확률 (0~1, >=0으로 정규화됨). */
  readonly prevChance: number;
  /** 다음(i+1) 모듈 효과 재발동 확률 (0~1, >=0으로 정규화됨). */
  readonly nextChance: number;
}

/**
 * `effect`의 `echoPrevChance`/`echoNextChance` 식을 현재 `level`로 평가한 {@link EchoMeta}를 반환한다.
 * effect 미정의 또는 필드 미기재 시 0, 음수는 0으로 정규화. {@link readEnhanceMeta}와 동일 패턴.
 */
export function readEchoMeta(
  effect: ModuleEffectData | undefined,
  level: number,
): EchoMeta {
  if (!effect) return { prevChance: 0, nextChance: 0 };
  const ev = (e: string | undefined): number => (e ? evaluateFormula(e, { level }) : 0);
  return {
    prevChance: Math.max(0, ev(effect.echoPrevChance)),
    nextChance: Math.max(0, ev(effect.echoNextChance)),
  };
}

/** 여러 effect의 echo 메타를 합산한다. 숙련도 effect가 이웃 재발동 확률을 보탤 때 사용한다. */
export function combineEchoMeta(
  effects: readonly (ModuleEffectData | undefined)[],
  level: number,
): EchoMeta {
  return effects.reduce<EchoMeta>(
    (acc, effect) => {
      const meta = readEchoMeta(effect, level);
      return {
        prevChance: acc.prevChance + meta.prevChance,
        nextChance: acc.nextChance + meta.nextChance,
      };
    },
    { prevChance: 0, nextChance: 0 },
  );
}

/** 경로순 한 모듈의 입력 — 자기 기본 등급 + 자기가 "다음 모듈"에 줄 강화 메타. */
export interface PipelineSlot {
  readonly baseRarity: ModuleRarity;
  readonly enhance: EnhanceMeta;
}

/** 강화 적용 결과 — 그 모듈에 실제로 적용할 유효 등급 + (이전 모듈이 준) 발동확률 가산. */
export interface ResolvedSlot {
  readonly rarity: ModuleRarity;
  readonly chanceBonus: number;
}

export interface PipelineSegmentOptions {
  /** true면 index와 index-1이 같은 흐름/segment로 이어져 있다고 본다. 미지정 시 기존처럼 모두 연결로 처리한다. */
  readonly sameSegmentAsPrevious?: (index: number) => boolean;
}

/**
 * 경로순 모듈 리스트에 "바로 앞 모듈의 강화"를 적용한 결과를 반환한다 (순수).
 * 각 모듈은 **직전(i-1) 모듈의 enhance**만 받는다(바로 다음 1개 규칙) — 첫 모듈/경로 끝 buff는 받을/줄 대상이 없다.
 * 강화 모듈이 연속이면 각자 자기 baseRarity 기준 메타를 다음에 적용한다(buff된 등급 기준 재계산은 하지 않음 — 단순·예측 가능).
 */
export function resolvePipelineEnhancements(
  slots: readonly PipelineSlot[],
  options: PipelineSegmentOptions = {},
): ResolvedSlot[] {
  return slots.map((slot, i) => {
    const prev = i > 0 && (options.sameSegmentAsPrevious?.(i) ?? true) ? slots[i - 1] : undefined;
    if (!prev) return { rarity: slot.baseRarity, chanceBonus: 0 };
    return {
      rarity: raiseRarity(slot.baseRarity, prev.enhance.rarityDelta),
      chanceBonus: prev.enhance.chanceBonus,
    };
  });
}

/**
 * 경로순 슬롯 + 인스턴스 정보에서 echo 확률 배열을 산출한다 (순수 — 단위테스트 대상).
 *
 * 규칙: 경계(i=0→prevP=0, i=last→nextP=0) + 이웃 fused → 해당 방향 0 + **발신자 fused → 양방향 0**.
 * 발신자 fused는 {@link readEchoMeta}가 effect undefined로 이미 0을 반환해 간접 차단되지만,
 * 이 함수가 slots(echo 값)만 받아도 계약이 깨지지 않도록 발신자 fused를 명시적으로 방어한다(opaque 정책의 단일 SSoT).
 */
export function computeEchoChances(
  slots: readonly { readonly echo: EchoMeta }[],
  insts: readonly { readonly fused?: unknown }[],
  options: PipelineSegmentOptions = {},
): { echoPrevChances: number[]; echoNextChances: number[] } {
  return {
    echoPrevChances: slots.map((s, i) =>
      i > 0 && (options.sameSegmentAsPrevious?.(i) ?? true) && !insts[i]!.fused && !insts[i - 1]!.fused
        ? s.echo.prevChance
        : 0,
    ),
    echoNextChances: slots.map((s, i) =>
      i < insts.length - 1
        && (options.sameSegmentAsPrevious?.(i + 1) ?? true)
        && !insts[i]!.fused
        && !insts[i + 1]!.fused
        ? s.echo.nextChance
        : 0,
    ),
  };
}
